import type { ComponentInterface, EventEmitter } from '@stencil/core';
import { Component, Host, h, Prop, Element, Method, Event } from '@stencil/core';

@Component({
  tag: 'jackery-toast',
  styleUrl: 'jackery-toast.css',
  shadow: true,
})
export class JackeryToast implements ComponentInterface {
  @Element() el!: HTMLJackeryToastElement;
  private mainEl?: HTMLElement;
  /**
   * 开启
   */
  @Prop({ mutable: true }) show = false;

  /**
   * 类型
   */
  @Prop({ reflect: true }) type: 'success' | 'info' | 'warning' | 'error' = 'success';

  /**
   * 是否显示ICON
   */
  @Prop({ reflect: true }) showIcon: boolean = true;

  /**
   * 标题
   */
  @Prop() header?: string;

  /**
   * 辅助标题
   */
  @Prop() subHeader?: string;

  /**
   * 展示时长(ms)，值为 0 时，toast 不会消失
   */
  @Prop() duration?: number = 3000;

  /**
   * 定时器
   * 在duration存在时有效
   */
  @Prop({ mutable: true }) timer?: ReturnType<typeof setTimeout> = setTimeout(() => {});

  /**
   * 关闭时是否将dom从文档中移除
   */
  @Prop() dRemove?: boolean = true;

  /**
   * 是否显示关闭按钮
   */
  @Prop() showClose?: boolean = true;

  /**
   * 是否呈现卡片形式（操作反馈）
   */
  @Prop() isCard?: boolean = true;

  /**
   * toast关闭调用
   */
  @Event({ eventName: 'jkyToastClose' }) toastClose!:EventEmitter;

   /**
    * toast开启调用
    */
  @Event({ eventName: 'jkyToastOpen' }) toastOpen!:EventEmitter;

  @Method()
  async hide () {
    this.show = false
  }

  @Method()
  async open() {
    this.show = true
  }

  /**
   * 获取提示类型svg icon path
   */
  private renderPath = () => {
    const { type } = this;
    switch (type) {
      case 'success':
        return <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path>
      case 'error':
        return <path d="M512 64c247.4 0 448 200.6 448 448S759.4 960 512 960 64 759.4 64 512 264.6 64 512 64zm127.98 274.82h-.04l-.08.06L512 466.75 384.14 338.88c-.04-.05-.06-.06-.08-.06a.12.12 0 00-.07 0c-.03 0-.05.01-.09.05l-45.02 45.02a.2.2 0 00-.05.09.12.12 0 000 .07v.02a.27.27 0 00.06.06L466.75 512 338.88 639.86c-.05.04-.06.06-.06.08a.12.12 0 000 .07c0 .03.01.05.05.09l45.02 45.02a.2.2 0 00.09.05.12.12 0 00.07 0c.02 0 .04-.01.08-.05L512 557.25l127.86 127.87c.04.04.06.05.08.05a.12.12 0 00.07 0c.03 0 .05-.01.09-.05l45.02-45.02a.2.2 0 00.05-.09.12.12 0 000-.07v-.02a.27.27 0 00-.05-.06L557.25 512l127.87-127.86c.04-.04.05-.06.05-.08a.12.12 0 000-.07c0-.03-.01-.05-.05-.09l-45.02-45.02a.2.2 0 00-.09-.05.12.12 0 00-.07 0z"></path>
      case 'info':
        return <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 010-96 48.01 48.01 0 010 96z"></path>
      case 'warning':
        return <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm32 664c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V456c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272zm-32-344a48.01 48.01 0 010-96 48.01 48.01 0 010 96z"></path>
      default:
        return <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z"></path>
    }
  }

  /**
   * 渲染关闭按钮图标
   */
  private renderCloseSVG = () => {
    return (
      <svg class="icon-close" fill-rule="evenodd" viewBox="64 64 896 896" focusable="false" data-icon="close" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M799.86 166.31c.02 0 .04.02.08.06l57.69 57.7c.04.03.05.05.06.08a.12.12 0 010 .06c0 .03-.02.05-.06.09L569.93 512l287.7 287.7c.04.04.05.06.06.09a.12.12 0 010 .07c0 .02-.02.04-.06.08l-57.7 57.69c-.03.04-.05.05-.07.06a.12.12 0 01-.07 0c-.03 0-.05-.02-.09-.06L512 569.93l-287.7 287.7c-.04.04-.06.05-.09.06a.12.12 0 01-.07 0c-.02 0-.04-.02-.08-.06l-57.69-57.7c-.04-.03-.05-.05-.06-.07a.12.12 0 010-.07c0-.03.02-.05.06-.09L454.07 512l-287.7-287.7c-.04-.04-.05-.06-.06-.09a.12.12 0 010-.07c0-.02.02-.04.06-.08l57.7-57.69c.03-.04.05-.05.07-.06a.12.12 0 01.07 0c.03 0 .05.02.09.06L512 454.07l287.7-287.7c.04-.04.06-.05.09-.06a.12.12 0 01.07 0z"></path></svg>
    )
  }

  /**
   * 关闭处理
   */
   private handleClose = () => {
     this.show = false
    try {
      if(this.timer) clearTimeout(this.timer)
    } catch (error) {
      // ERROR TODE...
    }
  }

  componentDidLoad() {
    this.mainEl.addEventListener("transitionend", () => {
      // 有两次动画：开始和结束，在结束动画完成时候的时候在移除节点
      if(!this.show) {
        this.toastClose.emit();
        if(this.dRemove) {
          try {
            document.body.removeChild(this.el);
          } catch (error) {
            console.error(`JACKERY-TOAST CLOSE ERROR: ${error}`)
          }
        }
      } else {
        this.toastOpen.emit()
      }
    })
  }

  render() {
    const {type, showIcon, subHeader, header, show, showClose, isCard} = this
    return (
      <Host
        class={{
          [`toast-${type}`]: true,
          [`toast-icon-show`]: showIcon,
          [`toast-has-subtitle`]: !!subHeader,
          [`toast-open`]: show,
          [`toast-card`]: isCard,
          [`toast-closeable`]: showClose,
        }}
      >

        <div class="toast-container" ref={(mainEl) => (this.mainEl = mainEl)}>
          {
            showIcon && <svg class="toast--icon" viewBox="64 64 896 896" focusable="false" data-icon="info-circle" width="1em" height="1em" fill="currentColor" aria-hidden="true">{this.renderPath()}</svg>
          }
          <div class="toast-content">
            <div class="toast-header">
              {header && (
                <h2 class="toast-title">{header}</h2>
              )}

              {subHeader && (
                <h3 class="toast-subtitle">{subHeader}</h3>
              )}
            </div>
            <slot></slot>
          </div>

          {
            showClose && (
              <div onClick={() => this.handleClose()} class="close-container">{this.renderCloseSVG()}</div>
            )
          }
        </div>
      </Host>
    );
  }
}
